import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon, Sun, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { DatePickerProps } from "./types";
import { useState, useCallback, useEffect, useMemo } from "react";
import { startOfDay, addDays, isToday, isTomorrow, format } from "date-fns";
import { useIsMobile } from "@/hooks/useIsMobile";
import { MobileActionSheet } from "@/components/MobileActionSheet";

type PanelProps = {
  date: Date | null;
  calendarMonth: Date;
  setCalendarMonth: (d: Date) => void;
  onSelect: (d: Date | undefined) => void;
  onShortcut: (d: Date | null) => (e: React.MouseEvent) => void;
  friendlyDateLabel: string;
  onDone: (e: React.MouseEvent) => void;
};

// Shared between the desktop popover and the mobile bottom sheet
const DatePickerPanel = ({ date, calendarMonth, setCalendarMonth, onSelect, onShortcut, friendlyDateLabel, onDone }: PanelProps) => (
  <>
    {/* Quick-schedule shortcuts */}
    <div className="flex items-center gap-1.5 px-3 pt-3 pb-2 dark:bg-muted max-md:dark:bg-transparent">
      <button
        type="button"
        onClick={onShortcut(startOfDay(new Date()))}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors max-md:py-1.5 max-md:text-sm",
          "border hover:bg-blue-50 dark:hover:bg-blue-500/10",
          date && isToday(date)
            ? "bg-blue-100 text-blue-700 border-transparent dark:bg-blue-500/15 dark:text-blue-300 dark:border-transparent"
            : "text-muted-foreground border-border"
        )}
      >
        <Sun className="h-3 w-3" />
        Today
      </button>
      <button
        type="button"
        onClick={onShortcut(startOfDay(addDays(new Date(), 1)))}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors max-md:py-1.5 max-md:text-sm",
          "border hover:bg-blue-50 dark:hover:bg-blue-500/10",
          date && isTomorrow(date)
            ? "bg-blue-100 text-blue-700 border-transparent dark:bg-blue-500/15 dark:text-blue-300 dark:border-transparent"
            : "text-muted-foreground border-border"
        )}
      >
        <Sun className="h-3 w-3" />
        Tomorrow
      </button>
      <button
        type="button"
        onClick={onShortcut(null)}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors max-md:py-1.5 max-md:text-sm",
          "border hover:bg-blue-50 dark:hover:bg-blue-500/10",
          !date
            ? "bg-blue-100 text-blue-700 border-transparent dark:bg-blue-500/15 dark:text-blue-300 dark:border-transparent"
            : "text-muted-foreground border-border"
        )}
      >
        <X className="h-3 w-3" />
        No date
      </button>
    </div>

    <div className="max-md:flex max-md:justify-center">
      <Calendar
        className="dark:bg-muted max-md:dark:bg-transparent"
        mode="single"
        selected={date || undefined}
        onSelect={onSelect}
        month={calendarMonth}
        onMonthChange={setCalendarMonth}
        initialFocus
      />
    </div>

    {/* Footer with date label and Done button */}
    <div className="flex items-center justify-between px-3 py-2.5 dark:bg-muted max-md:dark:bg-transparent">
      <span className="text-xs text-muted-foreground">
        {friendlyDateLabel}
      </span>
      <Button
        size="sm"
        className="h-7 px-3 text-xs bg-blue-600 hover:bg-blue-700 text-white dark:bg-blue-500 dark:hover:bg-blue-400 max-md:h-9 max-md:px-5 max-md:text-sm"
        onClick={onDone}
      >
        Done
      </Button>
    </div>
  </>
);

export const DatePickerControl = ({ date, setDate, onOk, onOpenChange }: DatePickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState<Date>(date || new Date());
  const isMobile = useIsMobile();
  useEffect(() => {
    if (onOpenChange) {
      onOpenChange(isOpen);
    }
  }, [isOpen, onOpenChange]);

  // Keep calendar month in sync when the popover opens
  useEffect(() => {
    if (isOpen) {
      setCalendarMonth(date || new Date());
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDateSelect = useCallback((selectedDate: Date | undefined) => {
    setDate(selectedDate || null);
    setIsOpen(true);
  }, [setDate]);

  const handleShortcut = useCallback((newDate: Date | null) => {
    return (e: React.MouseEvent) => {
      e.stopPropagation();
      setDate(newDate);
      if (newDate) {
        setCalendarMonth(newDate);
      }
      setIsOpen(false);
      onOk(newDate);
    };
  }, [setDate, onOk]);

  const handleDone = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(false);
    onOk(date);
  }, [onOk, date]);

  const friendlyDateLabel = useMemo(() => {
    if (!date) return "No date selected";
    if (isToday(date)) return "Today";
    if (isTomorrow(date)) return "Tomorrow";
    return format(date, "MMM d, yyyy");
  }, [date]);

  const triggerButton = (
    <Button
      variant="outline"
      size="sm"
      className={cn(
        "rounded-full justify-start text-left font-normal text-xs hover:bg-accent my-2 px-4 w-fit bg-transparent",
        date
          ? "text-blue-600 border-blue-200 dark:text-blue-400 dark:border-blue-500/20"
          : "text-muted-foreground"
      )}
      onClick={(e) => {
        e.stopPropagation();
        setIsOpen(true);
      }}
    >
      <CalendarIcon className="mr-2 h-4 w-4" />
      {date ? date.toLocaleString().split(",")[0] : <span>Date</span>}
    </Button>
  );

  const panel = (
    <DatePickerPanel
      date={date}
      calendarMonth={calendarMonth}
      setCalendarMonth={setCalendarMonth}
      onSelect={handleDateSelect}
      onShortcut={handleShortcut}
      friendlyDateLabel={friendlyDateLabel}
      onDone={handleDone}
    />
  );

  if (isMobile) {
    return (
      <>
        {triggerButton}
        <MobileActionSheet open={isOpen} onOpenChange={setIsOpen} title="Due date" srOnlyTitle>
          <div onClick={(e) => e.stopPropagation()}>{panel}</div>
        </MobileActionSheet>
      </>
    );
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        {triggerButton}
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0"
        onMouseDown={(e) => {
          e.preventDefault();
        }}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {panel}
      </PopoverContent>
    </Popover>
  );
};
